import * as React from 'react';
import { ReactNode } from 'react';
import { ValidationResult } from '../../../../util/validation/interfaces';
import { ApiResponse, ExternalIdDTO, ReferencedProjectDTO, SolrDocument, SuggestApi } from '../../../../api/core';
import { Suggester } from './Suggester';
import { ExternalId } from '../../../detail/components/ExternalId';
import { renderSelectedTemplate, topOrgaIcons } from './utils';

interface Props {
  value: SelectedProject[];
  onChange?: (values: SelectedProject[]) => void;
  multiple: boolean;
  disabled?: boolean;
  required?: boolean;
  validation?: ValidationResult;
  inputId?: string;
  autoFocus?: boolean;
  border?: 'border-noround' | 'border-noround-right';
  tooltip?: string;
  tooltipOptions?: any;
}

export interface SelectedProject {
  id: number;
  name: string;
  topOrgas: string[];
  aka: string[];
  externalIds: ExternalIdDTO[];
}

interface SolrProject extends SolrDocument {
  id: string;
  f_top_orga?: string[];
  project_name?: string;
  other_name?: string[];
  gnd_id?: string[];
  orcid_id?: string[];
}

// TODO remove "(referenced as any) when after ReferencedProject getting props topOrgas
export const referencedProjectToSelectedProject = (referenced: ReferencedProjectDTO): SelectedProject => {
  return {
    id: referenced.id,
    name: referenced.name,
    topOrgas: referenced.topOrgas || [],
    aka: [],
    externalIds: [],
  };
};

export const selectedProjectToReferencedProject = (selected: SelectedProject): ReferencedProjectDTO => {
  return {
    id: selected.id,
    name: selected.name,
    topOrgas: selected.topOrgas,
  } as ReferencedProjectDTO;
};

export function ProjectSuggester(props: Props): ReactNode {
  const solrToSelectedProject = (solr: SolrProject): SelectedProject => {
    return {
      id: +solr.id,
      name: solr.project_name,
      aka: solr.other_name || [],
      topOrgas: solr.f_top_orga || [],
      externalIds: [
        ...(solr.gnd_id || []).map((externalId: string): ExternalIdDTO => ({ externalId, type: 'gnd' })),
        ...(solr.orcid_id || []).map(
          (externalId: string): ExternalIdDTO => ({
            externalId,
            type: 'orcid',
          })
        ),
      ],
    };
  };

  const request = (params: { q: string }): Promise<ApiResponse<SolrDocument[]>> =>
    new SuggestApi().projectSuggestRaw(params);

  const selectedItemTemplate = (item: SelectedProject): ReactNode => {
    if(!item.topOrgas) {
    return renderSelectedTemplate(`${item.name} (${item.id})`);
    }
    const icons: { icon: string; title: string }[] = topOrgaIcons(item.topOrgas);
    return renderSelectedTemplate(`${item.name} (${item.id})`, icons);
  };

  const itemTemplate = (item: SelectedProject): ReactNode => {
    const icons: { icon: string; title: string }[] = topOrgaIcons(item.topOrgas);
    return (
      <div className={'max-w-30rem flex'}>
        <div className={'w-1 flex flex-column gap-1'}>
          {icons.length > 0 &&
            icons.map((icon: { icon: string; title: string }) => (
              <img src={icon.icon} alt={`${icon.title} Logo Icon`} className={'max-w-2rem'} />
            ))}
        </div>
        <div className={'w-11 flex flex-column gap-1'}>
          <div className={'flex flex-column white-space-normal'}>
            <span>
              <strong>{item.name}</strong> ({item.id})
            </span>
            {item.aka && item.aka.length > 0 && <small>{item.aka.join(', ')}</small>}
          </div>
          <div className={'flex flex-column white-space-normal'}>{item.topOrgas && [...item.topOrgas].join(', ')}</div>
          {item.externalIds && item.externalIds.length > 0 && (
            <div className={'flex gap-1'}>
              {(item.externalIds || []).map((externalId: ExternalIdDTO, idx: number) => (
                <div key={idx}>
                  <ExternalId id={{ ...externalId }} entity={'project'} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Suggester<SelectedProject, SelectedProject>
      itemTemplate={itemTemplate}
      autoFocus={props.autoFocus}
      selectedItemTemplate={selectedItemTemplate}
      load={request}
      map={solrToSelectedProject}
      inputId={props.inputId}
      value={props.value}
      disabled={props.disabled}
      validation={props.validation}
      required={props.required}
      onChange={props.onChange}
      border={props.border}
      tooltip={props.tooltip}
      tooltipOptions={props.tooltipOptions}
      multiple={props.multiple}/>
  );
}
